import { CustomizerEngine, ShapeLayer } from 'smg/customizer';

export interface BackgroundColorState {
    enabled: boolean;
    color: string;
}

// Canvas dimensions matching the coin customizer
const CANVAS_SIZE = 969;
const CENTER = CANVAS_SIZE / 2;

// Radius when text ring is enabled (inner image area only)
export const BG_RADIUS_WITH_RING = 322;
// Radius when text ring is disabled (full image area)
export const BG_RADIUS_NO_RING = 408;

/**
 * Manages a solid-color circle behind user images on a coin side.
 * Positioned just above the plating background layer (same approach as CoinTextRing).
 */
export class CoinBackgroundColor {
    #engine: CustomizerEngine;
    #shapeLayer: ShapeLayer;
    #enabled: boolean = false;
    #color: string = '#FFFFFF';
    #radius: number = BG_RADIUS_NO_RING;

    constructor(engine: CustomizerEngine, side: 'front' | 'back') {
        this.#engine = engine;

        this.#shapeLayer = new ShapeLayer(`${side}-bg-color`, {
            userEditable: false,
            resizable: false,
            zIndex: 1,
            canvasSize: CANVAS_SIZE,
        });

        void this.#engine.addLayer(`${side}-bg-color`, this.#shapeLayer);

        this.#positionLayer();
    }

    /**
     * Position the shape layer after background (layer 0), just like CoinTextRing.
     */
    #positionLayer(): void {
        this.#shapeLayer.sendToBack();
        this.#shapeLayer.moveForward();
    }

    show(): void {
        this.#enabled = true;
        this.#render();
    }

    hide(): void {
        this.#enabled = false;
        this.#shapeLayer.setVisible(false);
    }

    setColor(color: string): void {
        this.#color = color;
        if (this.#enabled) {
            this.#render();
        }
    }

    setRadius(radius: number): void {
        this.#radius = radius;
        if (this.#enabled) {
            this.#render();
        }
    }

    getColor(): string {
        return this.#color;
    }

    isEnabled(): boolean {
        return this.#enabled;
    }

    #render(): void {
        if (!this.#enabled) {
            return;
        }

        this.#shapeLayer.setShape({
            type: 'circle',
            radius: this.#radius,
            fill: this.#color,
            x: CENTER,
            y: CENTER,
        });

        this.#shapeLayer.setVisible(true);
    }

    getState(): BackgroundColorState {
        return {
            enabled: this.#enabled,
            color: this.#color,
        };
    }

    setState(state: BackgroundColorState): void {
        this.#color = state.color;
        if (state.enabled) {
            this.show();
        } else {
            this.hide();
        }
    }
}
